export type OptionTranslation = {
  language?: string | null;
  name?: string | null;
  description?: string | null;
  long_name?: string | null;
  longName?: string | null;
};

export type OptionTranslationsByLanguage = Record<string, OptionTranslation | undefined>;

export type TranslatableOption = {
  id?: number | string | null;
  typeId?: number | null;
  type_id?: number | null;
  typeName?: string | null;
  type_name?: string | null;
  language?: string | null;
  name?: string | null;
  description?: string | null;
  long_name?: string | null;
  longName?: string | null;
  translations_by_language?: OptionTranslationsByLanguage | null;
  translationsByLanguage?: OptionTranslationsByLanguage | null;
};

export type OptionDisplayTranslation = {
  language: string;
  name: string;
  description: string;
  longName: string;
};

const DEFAULT_LANGUAGE = "en";

const normalizeLanguage = (language?: string | null): string => {
  return (language || DEFAULT_LANGUAGE).toLowerCase().split(/[_-]/)[0] || DEFAULT_LANGUAGE;
};

const normalizeOptionalLanguage = (language?: string | null): string => {
  return language ? normalizeLanguage(language) : "";
};

const firstText = (...values: Array<string | null | undefined>): string => {
  return values.find((value) => typeof value === "string" && value.trim().length > 0) ?? "";
};

const getTranslationLongName = (translation?: OptionTranslation): string | null | undefined => {
  return translation?.long_name ?? translation?.longName;
};

const getRawOptionLongName = (option: TranslatableOption): string | null | undefined => {
  return option.longName ?? option.long_name;
};

const getRawOptionTypeName = (option: TranslatableOption): string | null | undefined => {
  return option.typeName ?? option.type_name;
};

const getTranslations = (option: TranslatableOption): OptionTranslationsByLanguage | null => {
  return option.translationsByLanguage ?? option.translations_by_language ?? null;
};

const findTranslation = (
  translations: OptionTranslationsByLanguage | null,
  language: string
): OptionTranslation | undefined => {
  if (!translations) return undefined;

  const normalizedLanguage = normalizeLanguage(language);
  const directMatch = translations[normalizedLanguage];
  if (directMatch) return directMatch;

  return Object.entries(translations).find(([key, translation]) => {
    return normalizeLanguage(key) === normalizedLanguage
      || normalizeOptionalLanguage(translation?.language) === normalizedLanguage;
  })?.[1];
};

export function hasOptionTranslations(option: TranslatableOption): boolean {
  return Object.keys(getTranslations(option) ?? {}).length > 0;
}

export function getOptionTranslation(option: TranslatableOption, language: string): OptionDisplayTranslation {
  const normalizedLanguage = normalizeLanguage(language);
  const translations = getTranslations(option);
  const localized = findTranslation(translations, normalizedLanguage);
  const english = findTranslation(translations, DEFAULT_LANGUAGE);

  const name = firstText(localized?.name, option.name, english?.name, getRawOptionTypeName(option));
  const description = firstText(localized?.description, option.description, english?.description);
  const longName = firstText(
    getTranslationLongName(localized),
    getRawOptionLongName(option),
    getTranslationLongName(english),
    name
  );

  return {
    language: normalizeLanguage(localized?.language ?? english?.language ?? option.language ?? normalizedLanguage),
    name,
    description,
    longName
  };
}

export function getOptionName(option: TranslatableOption, language: string): string {
  return getOptionTranslation(option, language).name;
}

export function getOptionDescription(option: TranslatableOption, language: string): string {
  return getOptionTranslation(option, language).description;
}

export function getOptionLongName(option: TranslatableOption, language: string): string {
  return getOptionTranslation(option, language).longName;
}

export function getOptionShortDescription(option: TranslatableOption, language: string): string {
  const description = getOptionDescription(option, language).trim();
  if (!description) return "";

  const sentenceEnd = description.indexOf(".");
  return sentenceEnd === -1 ? description : description.slice(0, sentenceEnd + 1).trim();
}

const optionIdentity = (option: TranslatableOption): string => {
  const typeId = option.typeId ?? option.type_id;
  if (typeId !== null && typeId !== undefined) return `type:${typeId}`;

  const id = option.id;
  if (id !== null && id !== undefined) return `id:${id}`;

  return `text:${option.name ?? ""}:${option.description ?? ""}`;
};

const languageScore = (option: TranslatableOption, language: string): number => {
  const normalizedLanguage = normalizeLanguage(language);
  const optionLanguage = normalizeOptionalLanguage(option.language);
  const translations = getTranslations(option);

  if (optionLanguage === normalizedLanguage) return 0;
  if (findTranslation(translations, normalizedLanguage)) return 1;
  if (optionLanguage === DEFAULT_LANGUAGE) return 2;
  if (findTranslation(translations, DEFAULT_LANGUAGE)) return 3;

  return 4;
};

export function selectOptionsForLanguage<T extends TranslatableOption>(options: T[], language: string): T[] {
  const byIdentity = new Map<string, { option: T; score: number }>();

  for (const option of options) {
    const identity = optionIdentity(option);
    const score = languageScore(option, language);
    const current = byIdentity.get(identity);

    if (!current || score < current.score) {
      byIdentity.set(identity, { option, score });
    }
  }

  return Array.from(byIdentity.values()).map((entry) => entry.option);
}
